import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { PendingPropertyTransferRequest, PropertyOwnership } from '@/client';
import { NOTIFICATIONS, showNotification } from '@/config/notifications.ts';
import { Routes } from '@/routes.ts';
import { useNavigate } from '@tanstack/react-router';
import { getMyOutgoingTransfersUsersMeOutgoingTransfersGetQueryKey } from '@/client/@tanstack/react-query.gen';
import { pendingTransferQueryKey } from '@/hooks/settings/usePendingTransfer';

import { TRANSFER_REASON_OWNER_TO_OWNER, TRANSFER_REASON_PROFESSIONAL_TO_OWNER } from '@/utils/pendingTransferUtils';

// Re-exported from pendingTransferUtils, where the transfer-reason domain rules live.
export { TRANSFER_REASON_OWNER_TO_OWNER, TRANSFER_REASON_PROFESSIONAL_TO_OWNER };

interface TransferFormValues {
	firstName: string;
	lastName: string;
	email: string;
	confirmEmail: string;
	message?: string;
}

interface UseTransferOwnershipProps {
	houseId: number;
	isBuilder: boolean;
}

export const TRANSFER_REASON_MAX_LENGTH = 64;
export const TRANSFER_EXPIRES_DAYS = 90;
/**
 * Hook for managing property ownership transfer functionality
 * Handles form state, validation, and API mutation for transferring property ownership
 */
export const useTransferOwnership = ({ houseId, isBuilder }: UseTransferOwnershipProps) => {
	const [showSuccess, setShowSuccess] = useState(false);
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	const {
		register,
		handleSubmit,
		watch,
		setFocus,
		formState: { errors, isValid },
	} = useForm<TransferFormValues>({
		mode: 'onTouched',
		defaultValues: {
			firstName: '',
			lastName: '',
			email: '',
			confirmEmail: '',
			message: '',
		},
	});

	const watchedFirstName = watch('firstName');
	const watchedLastName = watch('lastName');
	const watchedEmail = watch('email');
	const watchedConfirmEmail = watch('confirmEmail');

	const transferMutation = useMutation({
		mutationFn: async (data: PendingPropertyTransferRequest) => {
			return PropertyOwnership.createPendingPropertyTransferHousesHouseIdPendingTransferPost({
				path: { house_id: houseId },
				body: data,
				throwOnError: true,
			});
		},
		onSuccess: () => {
			setShowSuccess(true);

			// Invalidate the pending transfer query to refresh the status
			queryClient.invalidateQueries({ queryKey: pendingTransferQueryKey(houseId) });
			// Refresh the outgoing-transfers list so the property is badged "Transferring" on the
			// properties list without needing a manual refresh.
			queryClient.invalidateQueries({ queryKey: getMyOutgoingTransfersUsersMeOutgoingTransfersGetQueryKey() });

			navigate({ to: Routes.SETTINGS_PROPERTY, replace: true });
		},
		onError: (error) => {
			console.error('Transfer failed:', error);

			showNotification(NOTIFICATIONS.TRANSFER_OWNERSHIP.FAILED(error));
		},
	});

	const onSubmit = async (data: TransferFormValues, understood: boolean) => {
		if (data.email !== data.confirmEmail) {
			return;
		}

		if (!understood) {
			return;
		}

		// Validate transfer reason length (API limit is 64 characters)
		if (data.message && data.message.length > TRANSFER_REASON_MAX_LENGTH) {
			console.error(`Transfer reason cannot exceed ${TRANSFER_REASON_MAX_LENGTH} characters`);
			return;
		}

		// Automatically determine transfer reason based on user role
		const transferReason = isBuilder ? TRANSFER_REASON_PROFESSIONAL_TO_OWNER : TRANSFER_REASON_OWNER_TO_OWNER;
		const fullName = `${data.firstName.trim()} ${data.lastName.trim()}`.trim();

		const transferRequest: PendingPropertyTransferRequest = {
			to_email: data.email,
			to_name: fullName,
			transfer_reason: transferReason,
			transfer_payment_responsibility: true,
			expires_days: TRANSFER_EXPIRES_DAYS,
		};

		transferMutation.mutate(transferRequest);
	};

	const emailsMatch = watchedEmail === watchedConfirmEmail;
	const canSubmit = isValid && emailsMatch && !transferMutation.isPending;

	return {
		// Form state
		register,
		handleSubmit,
		setFocus,
		errors,
		watchedFirstName,
		watchedLastName,
		watchedEmail,
		watchedConfirmEmail,
		emailsMatch,

		// Transfer state
		showSuccess,
		transferMutation,
		canSubmit,

		// Actions
		onSubmit,
	};
};
